import React from 'react';
import {D3ChartAxis} from "../../platform/d3chart/D3ChartAxis";
import {D3ChartGridLines} from "../../platform/d3chart/D3ChartGridLines";
import {D3ChartLine} from "../../platform/d3chart/D3ChartLine";
import {curveToPoints} from "../../business/useImpellerSimulation";
import {D3ChartPoint} from "../../platform/d3chart/D3ChartPoint";
import {D3ChartScaleManager} from "../../platform/d3chart/D3ChartScaleManager";
import {D3ChartRenderable} from "../../platform/d3chart/D3Chart";
import {D3ChartComponent} from "../../platform/d3chart/D3ChartComponent";
import {MouseGuidesD3ChartRenderable} from "../../charting/MouseGuidesD3ChartRenderable";
import {HoverExplainerD3ChartRenderable} from "charting/HoverExplainerD3ChartRenderable";
import {D3ChartText} from "../../platform/d3chart/D3ChartText";
import {FlowCurve, FlowPoint} from "../../business/core";
import {UNIFIED_SIMULATION_SIZING} from "./PressureChart";

export function FanEfficiencyChart(props: {
    efficiencyCurve: FlowCurve,
    allAngleVariantsEfficiencyCurves: { angle: number, flowCurve: FlowCurve }[],
    workingPoint: FlowPoint,
    mainBladeAngleFormatted: string,
    globalBepPoint?: FlowPoint
}) {

    const chartSize = UNIFIED_SIMULATION_SIZING.CHART_SIZE;
    const chartMargins = UNIFIED_SIMULATION_SIZING.CHART_MARGINS;

    const flowAxis = new D3ChartAxis([0.0, -1.0], [0.0, chartSize.width-chartMargins.left-chartMargins.right], "bottom", 15, "d3-axis-slight-domain-line",0,0,"Q [m³/s] [m³/h]");
    const flowAxis_m3h = new D3ChartAxis([0, -1], [0, chartSize.width-chartMargins.left-chartMargins.right], "bottom", 7, "d3-axis-nodomainline", 20);
    const efficiencyAxis = new D3ChartAxis([-1.0, 0.0], [0.0, chartSize.height - chartMargins.top - chartMargins.bottom], "left", 10, "d3-axis-slight-domain-line",0,0,"η [%]");

    const verticalGridLines = new D3ChartGridLines(flowAxis.scale, flowAxis, "vertical", "d3-stroke-1 d3-opacity-05 d3-stroke-black");
    const horizontalGridLines = new D3ChartGridLines(efficiencyAxis.scale, efficiencyAxis, "horizontal", "d3-stroke-1 d3-opacity-05 d3-stroke-black");

    const alternativeEfficiencyLines = props.allAngleVariantsEfficiencyCurves
        .map(angleVariant => {
            const line = new D3ChartLine<{ flow: number, value: number }>(
                flowAxis.scale,
                efficiencyAxis.scale,
                curveToPoints(angleVariant.flowCurve),
                chPoint => chPoint.flow,
                chPoint => chPoint.value * 100,
                "d3-stroke-0_3 d3-stroke-black d3-opacity-50"
            );
            line.setDescriber(p => "... " + angleVariant.angle.toFixed(2) + "° " +
                                  p.flow.toFixed(2) + " m³/s " + (p.value * 100).toFixed(2) + "%");
            return line;
        });

    const efficiencyLine = new D3ChartLine<{ flow: number, value: number }>(
        flowAxis.scale,
        efficiencyAxis.scale,
        curveToPoints(props.efficiencyCurve),
        chPoint => chPoint.flow,
        chPoint => chPoint.value * 100,
        "d3-stroke-2 d3-stroke-primary");
    efficiencyLine.setDescriber(p => "Dobrany " + props.mainBladeAngleFormatted + "°: " +
                                     p.flow.toFixed(2) + " m³/s " + (p.value * 100).toFixed(2) + "%");

    const effPoints = curveToPoints(props.efficiencyCurve);
    const lastEffPoint = effPoints[effPoints.length - 1];
    const alphaLabel = new D3ChartText(flowAxis.scale, efficiencyAxis.scale,
        lastEffPoint.flow, lastEffPoint.value * 100, "α", 4, -5, "12px");

    const workingPoint = new D3ChartPoint(flowAxis.scale, efficiencyAxis.scale, props.workingPoint.flow, props.workingPoint.value * 100, 5, undefined, "d3-fill-primary");
    const globalBepPointRenderable = props.globalBepPoint === undefined ? [] : [new D3ChartPoint(flowAxis.scale, efficiencyAxis.scale, props.globalBepPoint.flow, props.globalBepPoint.value * 100, 5, "#0a0")];
    const globalBepLabel = props.globalBepPoint === undefined ? [] : [new D3ChartText(flowAxis.scale, efficiencyAxis.scale, props.globalBepPoint.flow, props.globalBepPoint.value * 100, "BEP", 8, 7, "12px", "#0a0")];
    const efficiencyScaleManager = new D3ChartScaleManager([efficiencyLine, ...alternativeEfficiencyLines], 1.2);
    const scaleManager_m3h = new D3ChartScaleManager([efficiencyLine, ...alternativeEfficiencyLines], 1.2 * 3600, flowAxis_m3h.scale, null);

    const renderables: D3ChartRenderable[] = [
        efficiencyScaleManager,
        scaleManager_m3h,
        flowAxis,
        flowAxis_m3h,
        efficiencyAxis,
        verticalGridLines,
        horizontalGridLines,
        ...alternativeEfficiencyLines,
        efficiencyLine,
        alphaLabel,
        ...globalBepPointRenderable,
        ...globalBepLabel,
        workingPoint,
        new MouseGuidesD3ChartRenderable(flowAxis.scale, efficiencyAxis.scale),
        new HoverExplainerD3ChartRenderable([...alternativeEfficiencyLines, efficiencyLine])
    ];

    return <D3ChartComponent width={chartSize.width} height={chartSize.height} chartRenderables={renderables}
                             margins={chartMargins}/>
}
